import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { authApiService } from "../../../../api/api";
import { IClient } from "../../../../types/clients.type";
import { OrderStatus } from "../../../../types/order.type";
import {
  IOrderProductsObj,
  IPhoneOrderCreateDto,
} from "../../../../types/phoneOrder.type";
import { IProduct } from "../../../../types/product.type";
import { ISelectsInput } from "../../../../types/selects.type";
import {
  calculateOrder,
  ICalculateOrder,
} from "../../../../utils/calculateOrder";
import {
  clientTypeSelector,
  createClientsOrderSelector,
} from "../../../../utils/createSelector";
import SelectChangeProdListInput from "../../../inputs/SelectChangeProdListInput/SelectChangedProdListInput";
import StepsHeader from "../../../StepsHeader/StepsHeader";
import ModalBottomButtons from "../buttons/ModakBottomButtons/ModalBottomButtons";
import EditInput from "../inputs/EditInput/EditInput";
import SelectInput, { SelectOption } from "../inputs/SelectInput/SelectInput";
import ViewInput from "../inputs/ViewInput/ViewInput";
import styles from "./styles.module.scss";

interface Props {
  onClose: () => void;
  onApply: () => void;
  clients: IClient[];
  products: IProduct[];
}

const AddPhoneOrderForm: React.FC<Props> = (props) => {
  const clientSelector = createClientsOrderSelector(props.clients);
  const [selectClient, setSelectClient] = useState<SelectOption>(
    clientSelector[0]
  );
  const [phone, setPhone] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [step, setStep] = useState<number>(1);
  const [selectedProducts, setSelectedProducts] = useState<ISelectsInput[]>([]);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<ICalculateOrder | null>(null);
  const [clientType, setClientType] = useState<SelectOption>(
    clientTypeSelector[0]
  );
  const [productPrices, setProductPrices] = useState<Record<string, string>>(
    {}
  );
  const [note, setNote] = useState<string>("");

  const getDefaultPrice = (product: IProduct) =>
    clientType.value === clientTypeSelector[0].value
      ? product.price.toString()
      : product.wholeSalePrice.toString();

  useEffect(() => {
    if (selectClient.value === "new") {
      setPhone("");
      setFullName("");
      setAddressLine1("");
      setAddressLine2("");
      setCity("");
      setState("");
      setPostalCode("");
      setCountry("");
    } else {
      const user = props.clients.find((user) => user.id === selectClient.value);
      if (user) {
        setPhone(user.phone);
        setFullName(user.fullName);
        user.adressLine1 && setAddressLine1(user.adressLine1);
        user.adressLine2 && setAddressLine2(user.adressLine2);
        user.city && setCity(user.city);
        user.state && setState(user.state);
        user.postalCode && setPostalCode(user.postalCode);
        user.country && setCountry(user.country);
      }
    }
  }, [selectClient.value, props.clients]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      setOrder(calculateOrder(selectedProducts, props.products));
    } else {
      setOrder(null);
    }
  }, [selectedProducts]);

  useEffect(() => {
    if (!order) {
      setProductPrices({});
      return;
    }

    setProductPrices(
      Object.fromEntries(
        order.products.map((item) => [
          item.product.id,
          getDefaultPrice(item.product),
        ])
      )
    );
  }, [order, clientType.value]);

  const calculatedCustomPrice =
    order?.products.reduce((acc, item) => {
      const price = Number(productPrices[item.product.id] || 0);
      return acc + price * item.count;
    }, 0) ?? 0;

  const onClickConfirmOrder = async () => {
    if (order) {
      const hasInvalidPrice = order.products.some((item) => {
        const price = Number(productPrices[item.product.id]);
        return (
          productPrices[item.product.id] === undefined ||
          productPrices[item.product.id] === "" ||
          isNaN(price) ||
          price < 0
        );
      });

      if (hasInvalidPrice) {
        toast.error("Please enter a valid price for each product");
        return;
      }

      const products: IOrderProductsObj[] = order.products.map((item) => ({
        productId: item.product.id,
        quantity: item.count,
        pricePerUnit: Number(productPrices[item.product.id]),
      }));

      const totalAmount = calculatedCustomPrice;

      try {
        const body: IPhoneOrderCreateDto = {
          clientPhone: phone,
          clientFullName: fullName,
          status: OrderStatus.PENDING,
          totalAmount,
          adressLine1: addressLine1,
          adressLine2: addressLine2,
          city,
          state,
          postalCode,
          country,
          orderProducts: products,
          type: clientType.value,
          customPrice: totalAmount,
          ...(note.trim() && { note: note.trim() }),
        };

        const response = await authApiService.createPhoneOrder(body);

        if (response) {
          toast.success("Order created successfully");
          props.onApply();
        }
      } catch (err: any) {
        console.error(err);
        toast.error(
          err.response?.data?.message || err.message || "Failed to create order"
        );
      }
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.stepContainer}>
          <StepsHeader
            step={step}
            fitstStepTitle="Add products"
            secondStepTitle="Add client"
            thirdStepTitle="Confirm order"
          />
        </div>
        {step === 1 ? (
          <>
            <form className={styles.form}>
              <SelectChangeProdListInput
                title="Products"
                isMultiSelect={true}
                values={selectedProducts}
                onChangeCount={(id, value) => {
                  const index = selectedProducts.findIndex(
                    (item) => item.id === id
                  );
                  if (index !== -1) {
                    const newState = [...selectedProducts];
                    newState[index].selectCount = value;
                    setSelectedProducts(newState);
                  }
                }}
                onClickActive={(value) => {
                  setSelectedProducts((prevState) =>
                    prevState.filter((item) => item.id !== value.id)
                  );
                }}
                onClickInactive={(value) => {
                  setSelectedProducts((prevState) => [...prevState, value]);
                }}
                search={search}
                setSearch={(value) => setSearch(value)}
                items={props.products.map((item) => ({
                  id: item.id,
                  name: `${item.name}, ${item.capacity}`,
                  subName: item.brand.name,
                  secondSubName: `${item.price} €`,
                  count:
                    item.batches.reduce(
                      (acc, item) => (acc += item.batchQuantity),
                      0
                    ) - item.reserv,
                  // selectCount: (() => {
                  //   if (order?.products) {
                  //     const res = order.products.find(
                  //       (prod) => prod.product.id === item.id
                  //     );
                  //     if (res) {
                  //       return res.count;
                  //     } else {
                  //       return 0;
                  //     }
                  //   }
                  // })(),
                }))}
              />

              {order &&
                order.products.map((product) => (
                  <>
                    <ViewInput
                      title="Product"
                      value={`${product.product.name}, ${product.product.capacity}`}
                    />
                    <div className={styles.infoContainer}>
                      <ViewInput
                        title="Quantity"
                        value={product.count.toString()}
                      />
                      <ViewInput
                        title="Price per unit"
                        value={`${product.product.price.toString()} €`}
                      />
                      {/* <ViewInput
                        title="Price"
                        value={`${
                          product.count * Number(product.product.price)
                        } €`}
                      /> */}
                    </div>
                  </>
                ))}
              {order && (
                <div className={styles.infoContainer}>
                  <ViewInput
                    title="Total units"
                    value={`${order.products.reduce(
                      (acc, item) => (acc += item.count),
                      0
                    )}`}
                  />
                  <ViewInput title="Total price" value={`${order.total} €`} />
                </div>
              )}
            </form>
            <ModalBottomButtons
              onPressBack={props.onClose}
              onPressApply={() => {
                if (!order || order.products.length === 0) {
                  toast.error("Add products to order");
                  return;
                }
                setStep(2);
              }}
              backTitle="Back"
              applyTitle="Next step"
              isNextIcon={true}
            />
          </>
        ) : step === 2 ? (
          <>
            <form className={styles.form}>
              <SelectInput
                title="Client"
                values={clientSelector}
                value={selectClient}
                onChange={(value) => setSelectClient(value)}
              />
              {selectClient.value !== "new" ? (
                <>
                  <ViewInput title="Phone number" value={phone} />
                  <ViewInput title="Full name" value={fullName} />
                </>
              ) : (
                <>
                  <EditInput
                    title="Phone number"
                    value={phone}
                    setValue={(value) => setPhone(value)}
                    isRequired
                  />
                  <EditInput
                    title="Full name"
                    value={fullName}
                    setValue={(value) => setFullName(value)}
                    isRequired
                  />
                </>
              )}
              <EditInput
                value={addressLine1}
                title="Address line 1"
                setValue={(value) => setAddressLine1(value)}
                isRequired={false}
              />
              <EditInput
                value={addressLine2}
                title="Address line 2"
                setValue={(value) => setAddressLine2(value)}
                isRequired={false}
              />
              <EditInput
                value={city}
                title="City"
                setValue={(value) => setCity(value)}
                isRequired={false}
              />
              <EditInput
                value={state}
                title="State"
                setValue={(value) => setState(value)}
                isRequired={false}
              />
              <EditInput
                value={postalCode}
                title="Code"
                setValue={(value) => setPostalCode(value)}
                isRequired={false}
              />
              <EditInput
                value={country}
                title="Country"
                setValue={(value) => setCountry(value)}
                isRequired={false}
              />
            </form>
            <ModalBottomButtons
              onPressBack={() => setStep(1)}
              onPressApply={() => setStep(3)}
              backTitle="Back"
              applyTitle="Next step"
              isNextIcon={true}
            />
          </>
        ) : (
          <>
            <form className={styles.form}>
              <ViewInput value={fullName} title="Client" />
              <ViewInput value={phone} title="Phone" />
              {addressLine1 && (
                <ViewInput value={addressLine1} title="Address line 1" />
              )}
              {addressLine2 && (
                <ViewInput value={addressLine2} title="Address line 2" />
              )}
              <div className={styles.infoContainer}>
                {city && <ViewInput value={city} title="City" />}
                {postalCode && <ViewInput value={postalCode} title="Code" />}
                {country && <ViewInput value={country} title="Country" />}
              </div>
              <SelectInput
                value={clientType}
                title="Client type"
                values={clientTypeSelector}
                onChange={(value) => setClientType(value)}
              />
              {order &&
                order.products.map((product) => (
                  <div key={product.product.id}>
                    <ViewInput
                      title="Product"
                      value={`${product.product.name}, ${product.product.capacity}`}
                    />
                    <div className={styles.infoContainer}>
                      <ViewInput
                        title="Quantity"
                        value={product.count.toString()}
                      />
                      <EditInput
                        title="Price per unit"
                        value={productPrices[product.product.id] ?? ""}
                        setValue={(value) => {
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            setProductPrices((prev) => ({
                              ...prev,
                              [product.product.id]: value,
                            }));
                          }
                        }}
                        isRequired
                      />
                    </div>
                  </div>
                ))}
              {order && (
                <div className={styles.infoContainer}>
                  <ViewInput
                    title="Total amount"
                    value={order.products
                      .reduce((acc, item) => (acc += item.count), 0)
                      .toString()}
                  />
                  <ViewInput
                    title="Custom price"
                    value={`${calculatedCustomPrice.toFixed(2)} €`}
                  />
                </div>
              )}
              <EditInput
                title="Note"
                value={note}
                setValue={(value) => setNote(value)}
                isRequired={false}
                isBig={true}
              />
            </form>
            <ModalBottomButtons
              onPressBack={() => setStep(2)}
              onPressApply={onClickConfirmOrder}
              backTitle="Back"
              applyTitle="Confirm order"
            />
          </>
        )}
      </div>
    </>
  );
};

export default AddPhoneOrderForm;
